import { useCallback, useEffect, useMemo, useRef } from "react";
import api from "../api";

const sessionKey = "mv_report_session";

const getSessionId = () => {
  let sid = sessionStorage.getItem(sessionKey);
  if (!sid) {
    sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(sessionKey, sid);
  }
  return sid;
};

/** Anonymous funnel tracking for the public report (no cookies, no 3rd party). */
export default function useReportTracking(token) {
  const sessionId = useMemo(() => (typeof window === "undefined" ? "anon" : getSessionId()), []);
  const sent = useRef(new Set());
  const startedAt = useRef(Date.now());

  const track = useCallback((event, { once = true, dwellMs = 0 } = {}) => {
    if (!token) return;
    if (once && sent.current.has(event)) return;
    sent.current.add(event);
    api.post(`/public/analisi/${token}/event`, { event, sessionId, dwellMs }).catch(() => {});
  }, [token, sessionId]);

  // report opened + dwell time on leave
  useEffect(() => {
    if (!token) return;
    track("report_aperto");
    const flush = () => {
      const dwellMs = Date.now() - startedAt.current;
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/analisi/${token}/event`;
      const payload = JSON.stringify({ event: "report_aperto", sessionId, dwellMs });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
      }
    };
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);
    // keep the dwell time fresh even if the final beacon never fires
    const beat = setInterval(() => {
      track("report_aperto", { once: false, dwellMs: Date.now() - startedAt.current });
    }, 20000);
    return () => {
      clearInterval(beat);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
    };
  }, [token, track, sessionId]);

  /** ref callback: fires `event` the first time the element is 40% visible. */
  const sentinel = useCallback((event) => (node) => {
    if (!node || sent.current.has(event)) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        track(event);
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(node);
  }, [track]);

  return { track, sentinel };
}
